import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Form, Input } from 'semantic-ui-react';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  clientError,
  firstError,
  firstResponseError,
  pruneServerErrors,
  invalidUrlCharacters, validationClasses,
} from '@/utils/errors';
import fields, { globalFieldLengths } from '@/constants/fields';
import { states } from '@/constants/lifecycle';
import urlDenyCharacters from '@/constants/validation';
import FormValidationError from '@/components/errors/FormValidationError';
import Dialog from '@/components/dialogs/Dialog';
import MarkdownEditor from '@/components/text-editors/MarkdownEditor';
import {
  hideSaveRole,
  createRole,
  updateActiveRoleProperties,
  updateRolesFlags,
} from '../actions';
import RolesDropdown from './RolesDropdown';
import { roleGroupings } from '../constants';
import RoleGroupingsDropdown from '../components/RoleGroupingsDropdown';

const SaveRoleDialog = ({
  basedOn,
  code,
  createErrors,
  createStatus,
  description,
  grouping,
  hide,
  name,
  postData,
  updateFlags,
  updateProperties,
  theme,
}) => {
  const intl = useIntl();
  const roleGroupingChange = (e, { value }) => {
    const userFlag = value === roleGroupings.USERS;
    const tokenFlag = value === roleGroupings.TOKENS;
    updateFlags('lookups', userFlag, tokenFlag, userFlag);
    updateProperties({ grouping: value });
  };

  const roleChange = (e, { value }) => {
    updateProperties(grouping === roleGroupings.USERS ? { role: value } : { tokenRole: value });
  };

  const validateForm = () => {
    const validationErrors = [];

    if (code && (isNumeric(code) || invalidUrlCharacters(code))) {
      validationErrors.push(
        clientError(
          { entity: 'role' },
          intl.formatMessage({ id: 'save.role.validation.code.title' }),
          intl.formatMessage({ id: 'validation.invalid.code' }, { characters: urlDenyCharacters }),
          [fields.CODE],
        ),
      );
    }

    if (name && name.length > globalFieldLengths.name) {
      validationErrors.push(
        clientError(
          { entity: 'role' },
          intl.formatMessage({ id: 'save.role.validation.name.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.name }),
          [fields.NAME],
        ),
      );
    }

    if (description && description.length > globalFieldLengths.description) {
      validationErrors.push(
        clientError(
          { entity: 'role' },
          intl.formatMessage({ id: 'save.role.validation.description.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.description }),
          [fields.DESCRIPTION],
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    updateProperties({ createErrors: validationErrors });

    return validationErrors;
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();

    if (validationErrors.length === 0) {
      postData(grouping === roleGroupings.TOKENS);
    }
  };

  const codeError = createErrors.find((e) => (e.fields.includes(fields.CODE)));
  const nameError = createErrors.find((e) => (e.fields.includes(fields.NAME)));
  const descriptionError = createErrors.find((e) => (e.fields.includes(fields.DESCRIPTION)));
  const roleError = createErrors.find((e) => (e.fields.includes(fields.ROLE)));

  return (
    <Dialog
      className="aridhia-save-role-dialog"
      error={firstResponseError(createErrors)}
      icon="drivers license"
      inProgress={createStatus === states.PENDING}
      minimumDataEntryMet={name}
      onClose={hide}
      onDismiss={() => updateProperties({ createErrors: pruneServerErrors(createErrors) })}
      onSave={submitForm}
      saveMessageId="save.role.create.button"
      theme={theme}
      title={intl.formatMessage({ id: 'save.role.create.title' })}
    >
      <Form autoComplete="off" onSubmit={submitForm} error={firstError(createErrors) !== null}>
        <FormValidationError
          error={codeError}
          htmlFor="role_code"
          labelId="save.role.code"
          position="right"
        >
          <Input
            id="role_code"
            size="large"
            disabled={createStatus === states.PENDING}
            type="custom"
            placeholder={intl.formatMessage({ id: 'save.role.code.placeholder' })}
            value={code || ''}
            maxLength={globalFieldLengths.code}
            onChange={(e) => updateProperties({ code: e.target.value })}
            className={validationClasses(codeError)}
          />
        </FormValidationError>
        <FormValidationError
          error={nameError}
          htmlFor="role_name"
          labelId="save.role.name"
          position="right"
        >
          <Input
            id="role_name"
            autoFocus
            disabled={createStatus === states.PENDING}
            type="custom"
            size="large"
            placeholder={intl.formatMessage({ id: 'save.role.name.placeholder' })}
            value={name}
            maxLength={globalFieldLengths.name}
            onChange={(e) => updateProperties({ name: e.target.value })}
            className={validationClasses(nameError)}
          />
        </FormValidationError>
        <FormValidationError
          htmlFor="role_groupings_dropdown"
          labelId="save.role.grouping"
          position="right"
        >
          <RoleGroupingsDropdown
            value={grouping}
            canEdit
            disabled={createStatus === states.PENDING}
            onChange={roleGroupingChange}
          />
        </FormValidationError>
        <FormValidationError
          error={roleError}
          htmlFor="role_dropdown"
          labelId="save.role.inherit"
          position="right"
        >
          <RolesDropdown
            category="lookups"
            value={basedOn}
            disabled={createStatus === states.PENDING}
            onChange={roleChange}
            withDescription
            withPermissions
            fluid
          />
        </FormValidationError>
        <FormValidationError
          error={descriptionError}
          htmlFor="role_description"
          labelId="save.role.description"
        >
          <MarkdownEditor
            id="role_description"
            disabled={createStatus === states.PENDING}
            value={description}
            onChange={(value) => updateProperties({ description: value })}
            maxEditorHeight={80}
            minPreviewHeight={94}
            className={validationClasses(descriptionError)}
          />
        </FormValidationError>
      </Form>
    </Dialog>
  );
};

const mapStateToProps = ({
  roles: {
    activeRole: {
      code, name, role, tokenRole, grouping, createErrors, createStatus, description,
    },
    roles: { lookups },
  },
}) => ({
  code,
  name,
  basedOn: lookups.userFlag ? role : tokenRole,
  grouping,
  createErrors,
  createStatus,
  description,
});

const mapDispatchToProps = {
  hide: hideSaveRole,
  updateProperties: updateActiveRoleProperties,
  postData: createRole,
  updateFlags: updateRolesFlags,
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveRoleDialog);
